import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const RhymeTimeGame = ({ gameData, onComplete }) => {
    const [selectedWords, setSelectedWords] = useState([]);
    const [matchedPairs, setMatchedPairs] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);

    const allWords = React.useMemo(() => {
        if (!gameData) return [];
        const words = [];
        gameData.pairs.forEach(pair => {
            words.push({ word: pair.word1, emoji: pair.emoji1, pairId: pair.id, type: 'word1' });
            words.push({ word: pair.word2, emoji: pair.emoji2, pairId: pair.id, type: 'word2' });
        });
        gameData.distractors?.forEach((dist, idx) => {
            words.push({ word: dist.word, emoji: dist.emoji, pairId: null, type: 'distractor', id: `dist-${idx}` });
        });
        return words.sort(() => Math.random() - 0.5);
    }, [gameData]);

    const handleWordClick = (wordObj) => {
        if (matchedPairs.includes(wordObj.pairId) || feedback) return;
        triggerHaptic(30);

        if (selectedWords.find(w => w.word === wordObj.word)) {
            setSelectedWords(selectedWords.filter(w => w.word !== wordObj.word));
            return;
        }

        const newSelected = [...selectedWords, wordObj];

        if (newSelected.length === 2) {
            const [first, second] = newSelected;
            setSelectedWords(newSelected);
            
            if (first.pairId && first.pairId === second.pairId) {
                setFeedback('correct');
                setMatchedPairs(prev => [...prev, first.pairId]);
                const pointsToAdd = gameData.points_per_pair || 15;
                const newScore = score + pointsToAdd;
                setScore(newScore);
                triggerHaptic([100, 50, 100]);

                setTimeout(() => {
                    setSelectedWords([]);
                    setFeedback(null);
                    if (matchedPairs.length + 1 >= gameData.pairs.length) {
                        triggerHaptic([200, 100, 200, 100, 300]);
                        onComplete?.(newScore);
                    }
                }, 3000);
            } else {
                setFeedback('wrong');
                triggerHaptic([50, 50, 50]);
                setTimeout(() => {
                    setSelectedWords([]);
                    setFeedback(null);
                }, 1500);
            }
        } else {
            setSelectedWords(newSelected);
        }
    };

    if (!gameData || !gameData.pairs) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-pink-100 animate-pulse bg-white">
                <div className="text-9xl mb-8">🎤</div>
                <h3 className="text-base md:text-3xl font-black text-pink-900 tracking-tighter uppercase">Ruošiami rimai...</h3>
            </div>
        );
    }

    const progress = (matchedPairs.length / gameData.pairs.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-rose-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:p-6">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-rose-100 rounded-2xl md:rounded-3xl flex items-center justify-center text-lg md:text-4xl shadow-inner border-2 border-rose-200 animate-float">
                        🎤
                    </div>
                    <div>
                        <h3 className="text-base md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                            Rimų <span className="text-rose-600">Pasaulis</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border-2 border-white shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 md:mb-2">Ritmas</p>
                        <p className="text-sm md:text-3xl font-black text-rose-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[80px] md:min-w-[120px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 md:mb-2">Poros</p>
                        <p className="text-sm md:text-3xl font-black text-slate-800 leading-none">{matchedPairs.length}/{gameData.pairs.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-8 w-full bg-slate-200/50 rounded-full h-2 md:h-4 p-0.5 md:p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-rose-400 via-pink-500 to-purple-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(244,63,94,0.4)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4 p-2">
            {allWords.map((wordObj, index) => {
                const isSelected = selectedWords.find(w => w.word === wordObj.word);
                const isMatched = matchedPairs.includes(wordObj.pairId);
                const isCorrectInPair = feedback === 'correct' && isSelected;
                const isWrongInPair = feedback === 'wrong' && isSelected;

                return (
                    <button
                        key={index}
                        onClick={() => handleWordClick(wordObj)}
                        disabled={isMatched || !!feedback}
                        className={`
                            relative flex flex-col items-center justify-center p-4 md:p-10 rounded-2xl md:rounded-[3rem] transition-all duration-500 transform border-b-8 shadow-xl group
                            ${isMatched
                                ? 'bg-emerald-500/10 opacity-30 scale-95 border-emerald-900/50'
                                : isCorrectInPair
                                    ? 'bg-emerald-500 text-white border-emerald-700 scale-105 z-20'
                                    : isWrongInPair
                                        ? 'bg-rose-500 text-white border-rose-700 animate-shake z-20'
                                        : isSelected
                                            ? 'bg-rose-600 text-white border-rose-800 scale-105'
                                            : 'bg-white text-slate-800 border-slate-100 hover:bg-rose-50 active:translate-y-1 active:border-b-0'
                            }
                        `}
                    >
                        <div className={`text-5xl md:text-8xl mb-3 transition-transform duration-500 ${isSelected ? 'animate-bounce' : 'group-hover:scale-110'}`}>
                            {wordObj.emoji}
                        </div>
                        <div className={`text-xs md:text-xl font-black tracking-tighter uppercase font-serif italic ${isSelected || (feedback && isSelected) ? 'text-white' : 'text-slate-800'}`}>
                            {wordObj.word}
                        </div>
                        {isMatched && (
                            <div className="absolute top-2 right-2 w-8 h-8 md:w-12 md:h-12 bg-emerald-500 text-white rounded-full flex items-center justify-center text-sm md:text-2xl shadow-xl animate-pop-in border-2 md:border-4 border-white">✓</div>
                        )}
                    </button>
                );
            })}
        </div>
    );

    const targets = (
        <div className={`
            transition-all duration-500
            ${selectedWords.length > 0 ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}
        `}>
            <div className="premium-glass bg-white/95 rounded-xl md:rounded-[3rem] p-3 md:p-6 shadow-2xl border-2 border-white flex flex-col items-center gap-2 md:gap-4 backdrop-blur-xl relative overflow-hidden">
                <div className="text-[8px] md:text-[10px] font-black text-rose-400 uppercase tracking-[0.5em]">Rimo Formavimas</div>
                <div className="flex items-center gap-2 md:gap-4">
                    {selectedWords.map((wordObj, idx) => (
                        <React.Fragment key={idx}>
                            {idx === 1 && <div className="text-lg md:text-4xl text-rose-300 font-black">+</div>}
                            <div className="flex items-center gap-2 bg-rose-50 px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-2xl border-2 border-rose-100 animate-pop-in shadow-inner">
                                <span className="text-xl md:text-4xl">{wordObj.emoji}</span>
                                <span className="font-black text-rose-800 text-sm md:text-xl uppercase italic font-serif">{wordObj.word}</span>
                            </div>
                        </React.Fragment>
                    ))}
                    {selectedWords.length === 1 && (
                        <>
                            <div className="text-lg md:text-4xl text-rose-200 font-black">+</div>
                            <div className="flex items-center justify-center w-12 h-12 md:w-20 md:h-20 bg-slate-50 rounded-xl md:rounded-2xl border-2 border-dashed border-rose-200 animate-pulse">
                                <span className="text-rose-200 font-black text-lg md:text-4xl">?</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 pointer-events-none flex items-center justify-center z-[2000] p-4">
                    <div className={`
                        px-8 py-6 md:px-16 md:py-12 rounded-[3rem] shadow-2xl backdrop-blur-3xl border-8 animate-pop-in text-center max-w-xl
                        ${feedback === 'correct' ? 'bg-emerald-500/90 border-emerald-400 text-white' : 'bg-rose-500/90 border-rose-400 text-white'}
                    `}>
                        <div className="text-7xl md:text-[120px] mb-4 md:mb-8 animate-bounce">
                            {feedback === 'correct' ? '🎙️' : '🔇'}
                        </div>
                        <h3 className="text-2xl md:text-5xl font-black tracking-tighter uppercase leading-none italic font-serif">
                            {feedback === 'correct' ? 'Tobulas Rimas!' : 'Nerimuoja...'}
                        </h3>
                    </div>
                </div>
            )}
        </>
    );
};

export default RhymeTimeGame;
